import React, { ReactElement } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Intl } from '@erecruit/core';
import over from 'lodash.over';

import {
  Button,
  Icon,
  H4,
  Text,
  Flex,
  FlexItem,
  Notification,
  styled,
  Tag,
} from '@erecruit/ui-kit';
import { notificationTypeVisualLookup } from '../helpers';

const { FormattedMessage, FormattedRelative, injectIntl } = Intl;

export interface LinkDefinition {
  to: string;
  text: string;
}

export interface BasicNotificationProps {
  type: number;
  link?: LinkDefinition;
  title: string;
  body: string | ReactElement;
  time: string;
  read: boolean;
  onOkText?: string | ReactElement;
  onCancelText?: string | ReactElement;
  onCancelClick: () => void;
  onOkClick: () => void;
  closeToast: () => void;
}

export interface NotificationContentProps {
  id?: string;
  link?: LinkDefinition;
  type: number;
  title: string;
  body: string | ReactElement;
  time: string;
  read: boolean;
  onMarkAsRead?: (notificationId: string) => void;
}

const FullWidthContainer = styled(Flex)({
  width: '100%',
});

const NotificationContent = injectIntl(
  ({
    id,
    link,
    body,
    title,
    time,
    intl,
    type,
    onMarkAsRead,
  }: NotificationContentProps & Intl.InjectedIntlProps) => {
    const notificationTypeVisuals = notificationTypeVisualLookup[type];
    return (
      <FullWidthContainer padding={3} gutter={2} gutterDirection="horizontal">
        <FlexItem flex={0}>
          <Tag color={notificationTypeVisuals.color} circle>
            <Icon
              size="large"
              color="bodyTertiary"
              type={notificationTypeVisuals.icon}
            />
          </Tag>
        </FlexItem>
        <FlexItem flex={1}>
          <Flex direction="column">
            <FlexItem>
              <Flex align="center">
                <FlexItem flex={1}>
                  <H4>{title}</H4>
                </FlexItem>
                {onMarkAsRead && id && (
                  <FlexItem flex={0}>
                    <Icon
                      size="small"
                      title={intl.formatMessage({
                        id: messageIds.vms.notification.markAsRead,
                      })}
                      type="close"
                      onClick={e => {
                        onMarkAsRead(id);
                        e.stopPropagation();
                      }}
                    />
                  </FlexItem>
                )}
              </Flex>
            </FlexItem>
            <FlexItem>
              {typeof body === 'string' ? <Text>{body}</Text> : body}
              <Text gutter={1} color="bodySecondary">
                <Icon type="time" textual />
                <Text color="inherit" inline>
                  <FormattedRelative value={time} />
                </Text>
              </Text>
            </FlexItem>
            {link && (
              <FlexItem>
                <Link to={link.to}>{link.text}</Link>
              </FlexItem>
            )}
          </Flex>
        </FlexItem>
      </FullWidthContainer>
    );
  },
);

const BasicNotification = ({
  title,
  body,
  link,
  time,
  type,
  read,
  onOkText = <FormattedMessage id={messageIds.vms.notification.okText} />,
  onCancelText = (
    <FormattedMessage id={messageIds.vms.notification.cancelText} />
  ),
  onCancelClick,
  onOkClick,
  closeToast,
}: BasicNotificationProps) => {
  const history = useHistory();
  return (
    <Notification>
      <NotificationContent
        type={type}
        title={title}
        body={body}
        time={time}
        read={read}
      />
      <Flex padding={2} gutter={2} justify="flex-end" divider="top">
        <Button onClick={over<void>(closeToast, onCancelClick)}>
          {onCancelText}
        </Button>
        {link ? (
          <Button
            onClick={over<void>(closeToast, onOkClick, () =>
              history.push(link.to),
            )}
          >
            {link.text}
          </Button>
        ) : (
          <Button onClick={over<void>(closeToast, onOkClick)}>
            {onOkText}
          </Button>
        )}
      </Flex>
    </Notification>
  );
};

BasicNotification.defaultProps = {
  onCancelClick: () => undefined,
  onOkClick: () => undefined,
  closeToast: () => undefined,
};

export { BasicNotification, NotificationContent };
